//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';
import { DateUtils } from 'unison';

import * as instagramCompetitorsApi from '@apis/instagram-competitors/instagramCompetitorsApi';
import {
  IInstagramCompetitorsMediaResponse,
  IInstagramCompetitorsStatsResponse,
} from '@apis/instagram-competitors/instagramCompetitorsApiModel';
import {
  CompetitorMediaSort,
  CompetitorsSortDir,
  CompetitorsSortKey,
  CompetitorsSortVar,
  CompetitorsTrendMetric,
  ManagedCompetitorStatus,
} from '@instagram-account/competitors/instagramCompetitorsConstants';
import {
  IInstagramCompetitor,
  IInstagramCompetitorWithMetrics,
} from '@instagram-account/redux/competitors/instagramCompetitorsModel';
import { IInstagramCompetitorsState } from '@instagram-account/redux/competitors/instagramCompetitorsModel';
import {
  generateManageToastText,
  getCompetitorsToAdd,
  getCompetitorsToRemove,
  getHashtagsFromSearch,
  getUpdatedManagedCompetitors,
  getUsernamesFromSearch,
} from '@instagram-account/redux/competitors/InstagramCompetitorsUtils';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import {
  ALTERNATE_TOASTS,
  GENERIC_FAILED_TO_FETCH_DATA_TOAST,
} from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import {
  IToastNotification,
  ToastType,
} from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import DateString from '@models/DateString';
import { IUnisonDateRange } from '@models/UnisonTypes';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import { queryClient } from '@queries/ReactQueryProvider';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

const initialState: IInstagramCompetitorsState = {
  dateRange: DateUtils.getDateRangeFromAlias('last7Days'),
  manage: {
    isManageModalOpen: false,
    competitors: [],
    searchString: undefined,
    searchResult: undefined,
  },
  trackedCompetitorsLimit: 5,
  trackedCompetitors: [],
  aggregateStats: undefined,
  insights: {
    insightsSortDir: CompetitorsSortDir.ASC,
    insightsSortKey: CompetitorsSortKey.ACCOUNT,
  },
  media: {
    items: [],
    filters: {
      offset: 0,
      searchString: undefined,
      competitorUsernames: PojoSet.empty(),
      orderBy: CompetitorMediaSort.MOST_RECENT,
    },
    stats: {
      totalPosts: 0,
      avgLikes: 0,
      avgComments: 0,
      avgFollowerEngRate: 0,
    },
  },
  trendChart: {
    metric: CompetitorsTrendMetric.FOLLOWER_ENG_RATE,
    accounts: PojoSet.empty(),
  },
  flags: {
    isEndOfContentReached: false,
    isFetchingCompetitorsAndStats: true,
    isFetchingCompetitorsLimit: true,
    isFetchingCompetitorSearch: false,
    isFetchingCompetitorsSave: false,
    isFetchingMedia: false,
  },
  errors: {},
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramCompetitors,
  initialState,
  reducers: {
    requestCompetitorsLimit(state) {
      state.flags.isFetchingCompetitorsLimit = true;
      state.errors.fetchCompetitorsLimitError = undefined;
    },
    receiveCompetitorsLimitSucceeded(state, action: PayloadAction<number>) {
      state.flags.isFetchingCompetitorsLimit = false;
      state.trackedCompetitorsLimit = action.payload;
    },
    receiveCompetitorsLimitFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingCompetitorsLimit = false;
      state.errors.fetchCompetitorsLimitError = action.payload;
    },
    requestCompetitorsAndStats(state) {
      state.flags.isFetchingCompetitorsAndStats = true;
      state.errors.fetchCompetitorsAndStatsError = undefined;
    },
    receiveCompetitorsAndStatsSucceeded(
      state,
      action: PayloadAction<IInstagramCompetitorsStatsResponse>,
    ) {
      state.flags.isFetchingCompetitorsAndStats = false;
      state.trackedCompetitors = action.payload.competitors;
      state.aggregateStats = action.payload.aggregateStats;
      // Set the initial trend chart and media competitors
      state.media.filters.competitorUsernames = PojoSet.from(
        action.payload.competitors.map((competitor) => competitor.username),
      );
      state.trendChart.accounts = PojoSet.from(
        action.payload.competitors
          .filter((competitor) => !competitor.isBrandOwned)
          .map((competitor) => competitor.username),
      );
    },
    receiveCompetitorsAndStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingCompetitorsAndStats = false;
      state.errors.fetchCompetitorsAndStatsError = action.payload;
    },
    updateManageSearchString(state, action: PayloadAction<string>) {
      state.manage.searchString = action.payload;
    },
    requestSearchForCompetitor(state) {
      state.flags.isFetchingCompetitorSearch = true;
      state.errors.fetchCompetitorSearchError = undefined;
    },
    receiveSearchForCompetitorSucceeded(
      state,
      action: PayloadAction<IInstagramCompetitor>,
    ) {
      state.flags.isFetchingCompetitorSearch = false;
      state.manage.searchResult = action.payload;
    },
    receiveSearchForCompetitorFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingCompetitorSearch = false;
      state.errors.fetchCompetitorSearchError = action.payload;
    },
    requestSaveCompetitors(state) {
      state.flags.isFetchingCompetitorsSave = true;
      state.errors.fetchCompetitorsSaveError = undefined;
    },
    receiveSaveCompetitorsSucceeded(state) {
      state.flags.isFetchingCompetitorsSave = false;
    },
    receiveSaveCompetitorsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingCompetitorSearch = false;
      state.errors.fetchCompetitorsSaveError = action.payload;
    },
    setCompetitorsDateRange(state, action: PayloadAction<IUnisonDateRange>) {
      state.dateRange = action.payload;
      state.media.filters.offset = 0;
      state.flags.isEndOfContentReached = false;
    },
    openManageModal(state) {
      state.manage.isManageModalOpen = true;
      state.manage.competitors = state.trackedCompetitors;
    },
    closeManageModal(state) {
      state.manage.isManageModalOpen = false;
      state.manage.searchString = undefined;
    },
    setManagedCompetitors(
      state,
      action: PayloadAction<IInstagramCompetitor[]>,
    ) {
      state.manage.competitors = action.payload;
    },
    setTrackedCompetitors(
      state,
      action: PayloadAction<IInstagramCompetitorWithMetrics[]>,
    ) {
      state.trackedCompetitors = action.payload;
    },
    updateInsightsSort(
      state,
      action: PayloadAction<{
        newSortDir: CompetitorsSortDir;
        newSortKey: CompetitorsSortKey;
      }>,
    ) {
      state.insights.insightsSortDir = action.payload.newSortDir;
      state.insights.insightsSortKey = action.payload.newSortKey;
    },
    updateTrendAccounts(state, action: PayloadAction<PojoSet<string>>) {
      state.trendChart.accounts = action.payload;
    },
    updateTrendMetric(state, action: PayloadAction<CompetitorsTrendMetric>) {
      state.trendChart.metric = action.payload;
    },
    requestMedia(state) {
      state.flags.isFetchingMedia = true;
      state.errors.fetchMediaError = undefined;
    },
    receiveMediaSucceeded(
      state,
      action: PayloadAction<{
        response: IInstagramCompetitorsMediaResponse;
        isAdditionalPage: boolean;
      }>,
    ) {
      state.flags.isFetchingMedia = false;
      // If this is an additional page of media, append media rather than replace it.
      state.media.items = action.payload.isAdditionalPage
        ? [...state.media.items, ...action.payload.response.media]
        : action.payload.response.media;
      state.media.stats = action.payload.response.stats;
      state.media.filters.offset = action.payload.response.offset;
      state.flags.isEndOfContentReached =
        action.payload.response.media.length === 0;
    },
    receiveMediaFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingMedia = false;
      state.errors.fetchMediaError = action.payload;
    },
    updateMediaFilterOrderBy(
      state,
      action: PayloadAction<CompetitorMediaSort>,
    ) {
      state.media.filters.orderBy = action.payload;
      state.media.filters.offset = 0;
      state.flags.isEndOfContentReached = false;
    },
    updateMediaFilterSearchString(state, action: PayloadAction<string>) {
      state.media.filters.searchString = action.payload;
      state.media.filters.offset = 0;
      state.flags.isEndOfContentReached = false;
    },
    updateMediaFilterCompetitorUsernames(
      state,
      action: PayloadAction<PojoSet<string>>,
    ) {
      state.media.filters.competitorUsernames = action.payload;
      state.media.filters.offset = 0;
      state.flags.isEndOfContentReached = false;
    },
  },
});

export const addCompetitor = createThunk(
  (competitor: IInstagramCompetitor) => async (dispatch, getState) => {
    const managedCompetitors =
      getState().instagram.competitors.manage.competitors;
    const newManagedCompetitors = getUpdatedManagedCompetitors(
      competitor,
      managedCompetitors,
      ManagedCompetitorStatus.TO_ADD,
    );
    dispatch(actions.setManagedCompetitors(newManagedCompetitors));
  },
);

export const removeCompetitor = createThunk(
  (competitor: IInstagramCompetitor) => async (dispatch, getState) => {
    const managedCompetitors =
      getState().instagram.competitors.manage.competitors;
    const newManagedCompetitors = getUpdatedManagedCompetitors(
      competitor,
      managedCompetitors,
      ManagedCompetitorStatus.TO_REMOVE,
    );
    dispatch(actions.setManagedCompetitors(newManagedCompetitors));
  },
);

export const updateInsightsSort = createThunk(
  (newSortDir: CompetitorsSortDir, newSortKey: CompetitorsSortKey) =>
    async (dispatch, getState) => {
      dispatch(actions.updateInsightsSort({ newSortDir, newSortKey }));
      const newTrackedCompetitors = [
        ...getState().instagram.competitors.trackedCompetitors,
      ].sort((a, b) => {
        const sortMultiplier = newSortDir === CompetitorsSortDir.DESC ? -1 : 1;
        if (newSortKey === CompetitorsSortKey.ACCOUNT) {
          return a.username.localeCompare(b.username) * sortMultiplier;
        } else {
          return (
            ((a.stats?.[CompetitorsSortVar[newSortKey]] ?? 0) -
              (b.stats?.[CompetitorsSortVar[newSortKey]] ?? 0)) *
            sortMultiplier
          );
        }
      });
      dispatch(actions.setTrackedCompetitors(newTrackedCompetitors));
    },
);

export const selectAllTrendAccounts = createThunk(
  () => async (dispatch, getState) => {
    const { trackedCompetitors } = getState().instagram.competitors;
    const competitorUsernames = trackedCompetitors
      .filter((competitor) => !competitor.isBrandOwned)
      .map((competitor) => competitor.username);
    dispatch(actions.updateTrendAccounts(PojoSet.from(competitorUsernames)));
  },
);

export const deselectAllTrendAccounts = createThunk(() => async (dispatch) => {
  dispatch(actions.updateTrendAccounts(PojoSet.empty()));
});

export const toggleTrendAccount = createThunk(
  (username: string) => async (dispatch, getState) => {
    const { accounts } = getState().instagram.competitors.trendChart;
    dispatch(
      actions.updateTrendAccounts(
        PojoSet.toggle(accounts, username, !PojoSet.has(accounts, username)),
      ),
    );
  },
);

export const getMedia = createThunk(() => async (dispatch, getState) => {
  dispatch(actions.requestMedia());
  const { media, dateRange } = getState().instagram.competitors;
  const { filters } = media;

  try {
    const isAdditionalPage = filters.offset > 0;
    const browserAdjustedDateRange = DateString.fromUnisonDateRange(dateRange);

    const response = await instagramCompetitorsApi.getCompetitorMedia({
      startTime: browserAdjustedDateRange.startDate,
      endTime: browserAdjustedDateRange.endDate,
      orderBy: filters.orderBy,
      competitorUsernames: PojoSet.toArray(filters.competitorUsernames),
      searchUsernames: getUsernamesFromSearch(filters.searchString),
      hashtags: getHashtagsFromSearch(filters.searchString),
      searchText: filters.searchString,
      limit: 12,
      offset: filters.offset,
    });

    dispatch(actions.receiveMediaSucceeded({ response, isAdditionalPage }));
  } catch (e) {
    const err = getRequestErrorMessage(e) ?? '';
    dispatch(actions.receiveMediaFailed(err));
  }
});

export const selectAllMediaAccounts = createThunk(
  () => async (dispatch, getState) => {
    const { trackedCompetitors } = getState().instagram.competitors;
    const usernames = trackedCompetitors.map(
      (competitor) => competitor.username,
    );
    dispatch(
      actions.updateMediaFilterCompetitorUsernames(PojoSet.from(usernames)),
    );
  },
);

export const deselectAllMediaAccounts = createThunk(() => async (dispatch) => {
  dispatch(actions.updateMediaFilterCompetitorUsernames(PojoSet.empty()));
});

export const toggleMediaAccount = createThunk(
  (username: string) => async (dispatch, getState) => {
    const { competitorUsernames } =
      getState().instagram.competitors.media.filters;
    dispatch(
      actions.updateMediaFilterCompetitorUsernames(
        PojoSet.toggle(
          competitorUsernames,
          username,
          !PojoSet.has(competitorUsernames, username),
        ),
      ),
    );
  },
);

export const getCompetitorsAndStats = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestCompetitorsAndStats());
    const { dateRange } = getState().instagram.competitors;
    const browserAdjustedDateRange = DateString.fromUnisonDateRange(dateRange);

    try {
      const res = await instagramCompetitorsApi.getCompetitorsWithStats({
        startTime: browserAdjustedDateRange.startDate,
        endTime: browserAdjustedDateRange.endDate,
      });
      dispatch(actions.receiveCompetitorsAndStatsSucceeded(res));
      const { insightsSortDir, insightsSortKey } =
        getState().instagram.competitors.insights;
      dispatch(updateInsightsSort(insightsSortDir, insightsSortKey));
    } catch (e) {
      const err = getRequestErrorMessage(e) ?? '';
      dispatch(actions.receiveCompetitorsAndStatsFailed(err));

      dispatch(
        toastNotificationsActions.addToastNotification(
          GENERIC_FAILED_TO_FETCH_DATA_TOAST,
        ),
      );
    }
  },
);

export const searchForCompetitor = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestSearchForCompetitor);
    const { searchString } = getState().instagram.competitors.manage;
    const sanitizedUsername = searchString?.replace('@', '').trim();

    if (!sanitizedUsername) {
      dispatch(actions.receiveSearchForCompetitorSucceeded(undefined));
    } else {
      try {
        const searchResult =
          await instagramCompetitorsApi.getCompetitorForUsername(
            sanitizedUsername,
          );
        dispatch(
          actions.receiveSearchForCompetitorSucceeded(searchResult.competitor),
        );
      } catch (e) {
        const err = getRequestErrorMessage(e) ?? '';
        dispatch(actions.receiveSearchForCompetitorFailed(err));
      }
    }
  },
);

export const saveCompetitors = createThunk(() => async (dispatch, getState) => {
  dispatch(actions.requestSaveCompetitors());
  const { trackedCompetitors, manage } = getState().instagram.competitors;
  const { competitors } = manage;
  const competitorsToRemove = getCompetitorsToRemove(
    trackedCompetitors,
    competitors,
  );
  const competitorsToAdd = getCompetitorsToAdd(trackedCompetitors, competitors);

  try {
    await instagramCompetitorsApi.manageCompetitors(
      competitorsToAdd,
      competitorsToRemove,
    );
    dispatch(actions.receiveSaveCompetitorsSucceeded());
    dispatch(actions.closeManageModal());
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Competitors Updated',
        type: ToastType.SUCCESS,
        message: generateManageToastText(competitorsToAdd, competitorsToRemove),
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
      }),
    );

    if (queryClient) {
      await Promise.all([
        queryClient.invalidateQueries([
          QueryKeyConstants.MediaLibrary,
          QueryKeyConstants.MediaLibraryInstagram,
          QueryKeyConstants.MediaLibraryInstagramSettings,
          QueryKeyConstants.MediaLibraryInstagramTrackedCompetitors,
        ]),
        queryClient.invalidateQueries([QueryKeyConstants.Listening]),
      ]);
    }
  } catch (e) {
    const err = getRequestErrorMessage(e) ?? '';
    dispatch(actions.receiveSaveCompetitorsFailed(err));
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: 'Error',
        type: ToastType.ERROR,
        message: `Something went wrong, please try again.`,
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
      }),
    );
  }
});

export const getCompetitorsLimit = createThunk(() => async (dispatch) => {
  dispatch(actions.requestCompetitorsLimit);

  try {
    const res = await instagramCompetitorsApi.getCompetitorsLimit();
    dispatch(actions.receiveCompetitorsLimitSucceeded(res.limit));
  } catch (e) {
    const err = getRequestErrorMessage(e) ?? '';
    dispatch(actions.receiveCompetitorsLimitFailed(err));
  }
});

export const {
  setCompetitorsDateRange,
  openManageModal,
  closeManageModal,
  updateManageSearchString,
  updateTrendMetric,
  updateMediaFilterOrderBy,
  updateMediaFilterSearchString,
} = actions;

export default reducer;
